'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, Icon, Text, useTranslation, useUi } from '@weareconceptstudio/core';
import { Form, Input, useFormInstance } from '@weareconceptstudio/form';
import { useCart } from '@weareconceptstudio/cart';
import { useAccountContext, useAddressContext, AddressForm } from '@weareconceptstudio/account';
import Button from '@/components/Button';
import { withDataContext } from '@/context';

import DeliveryTemplateStyle from './style';
import CartModifiedWarningPopup from './CartModifiedWarningPopup';
import { DELIVERY_SEARCH_FIELD, PENDING_ID, useDeliveryTemplateSelector } from './useDeliveryTemplateSelector';
import { showFulfillmentRequiredPopup } from '@/helpers/fulfillmentGuard';
import { resolveRedirectRouteKey, resolveNavigationIntent, clearNavigationIntent, markFulfillmentRestorePending } from '@/helpers/fulfillmentNavigation';
import { useNavigateWithPageLoading, usePageLoading } from '@/hooks/usePageLoading';
import { formatAddressLabel, formatAddressSubtitle, formatAddressTitle } from '@/modules/Home/components/Hero/DeliveryBar/utils';

// Sentinel time-slot id representing an immediate ("as soon as possible") delivery.
// When selected, the order is placed with no scheduled slot and the backend treats
// it as an ASAP delivery.
const ASAP_SLOT_ID = 'asap';

const getTodayDateString = () => {
	const d = new Date();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${d.getFullYear()}-${month}-${day}`;
};

const toDateOptions = (dates = []) => (Array.isArray(dates) ? dates : []).filter((d) => d?.available !== false).map((d) => (typeof d === 'string' ? { id: d, name: d } : { id: d.date, name: d.label || d.date }));

const isDateInList = (dateStr, dates) => {
	if (!dateStr || !Array.isArray(dates) || !dates.length) return false;
	return dates.some((d) => (typeof d === 'string' ? d : d.date) === dateStr);
};

const readPendingAddress = () => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = sessionStorage.getItem('pending_address');
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const DeliveryTemplateContent = ({ initialTab, globalData, redirectAfterConfirm, onClose, variant = 'popup', autoConfirmWhenReady = false }) => {
	const navigateWithLoading = useNavigateWithPageLoading();
	const { startPageLoading, stopPageLoading } = usePageLoading();
	const { closePopup, openPopup } = useUi();
	const isDropdown = variant === 'dropdown';
	const { translate } = useTranslation();
	const { checkoutData, fillCheckoutData } = useCart();
	const { fulfillment, useUser } = useAccountContext();
	const { selectedAddresses, selectAddress, hasAddressType } = useAddressContext();
	const { isLoggedIn, user } = useUser();
	const formMethods = useFormInstance();

	const branches = fulfillment?.branches || [];

	const getAvailDates = (branchId = selectedBranchId, takeaway = false) => {
		if (takeaway && branchId && fulfillment?.takeaway_dates_by_branch?.[branchId]) {
			return fulfillment.takeaway_dates_by_branch[branchId];
		}
		return fulfillment?.scheduled_delivery_dates || fulfillment?.takeaway_dates || fulfillment?.dates;
	};

	const [activeTab, setActiveTab] = useState(() => initialTab || (checkoutData?.fulfillmentType === 'takeaway' ? 'takeaway' : 'delivery'));
	const [selectedBranchId, setSelectedBranchId] = useState(checkoutData?.fulfillmentBranchId || branches[0]?.id || null);
	const [selectedAddressId, setSelectedAddressId] = useState(() => {
		if (hasAddressType) return selectedAddresses?.shipping?.id || null;
		const realId = selectedAddresses?.id || null;
		if (!realId && !isLoggedIn && readPendingAddress()) return PENDING_ID;
		return realId;
	});
	const [selectedDate, setSelectedDate] = useState(() => {
		const initTakeaway = (initialTab || checkoutData?.fulfillmentType) === 'takeaway';
		const saved = checkoutData?.scheduledDate;
		const avail = getAvailDates(checkoutData?.fulfillmentBranchId || branches[0]?.id, initTakeaway);
		if (isDateInList(saved, avail)) return saved;
		return toDateOptions(avail)[0]?.id || null;
	});
	const [selectedTime, setSelectedTime] = useState(() => {
		const initTakeaway = (initialTab || checkoutData?.fulfillmentType) === 'takeaway';
		if (!initTakeaway && checkoutData?.fulfillmentType === 'asap') return ASAP_SLOT_ID;
		const savedDate = checkoutData?.scheduledDate;
		const avail = getAvailDates(checkoutData?.fulfillmentBranchId || branches[0]?.id, initTakeaway);
		const defaultDate = isDateInList(savedDate, avail) ? savedDate : toDateOptions(avail)[0]?.id;
		if (!initTakeaway && fulfillment?.asap_available && defaultDate === getTodayDateString()) {
			return ASAP_SLOT_ID;
		}
		return isDateInList(savedDate, avail) ? checkoutData?.scheduledTime || null : null;
	});
	const [timeSlots, setTimeSlots] = useState([]);
	const [slotsLoading, setSlotsLoading] = useState(false);
	const [showDateList, setShowDateList] = useState(false);
	const [showTimeList, setShowTimeList] = useState(false);
	const [pendingAddress, setPendingAddress] = useState(readPendingAddress);
	const [showSelectionError, setShowSelectionError] = useState(false);
	const initialPhone = checkoutData?.phone || user?.takeaway_phone || user?.phone || '';

	useEffect(() => {
		if (initialPhone) {
			formMethods.setValue('phone', initialPhone);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dateRef = useRef(null);
	const timeRef = useRef(null);

	const isTakeaway = activeTab === 'takeaway';

	const markAddressFormRestore = useCallback(() => {
		if (!isDropdown) {
			markFulfillmentRestorePending({ initialTab: isTakeaway ? 'takeaway' : 'delivery' });
		}
	}, [isDropdown, isTakeaway]);

	const {
		dropdownRef,
		showDropdown,
		isLocating,
		isSuggestLoading,
		isSearching,
		addressList,
		addressLoading,
		addressSuggestions,
		takeawayPlaceholder,
		handleBranchSelect,
		handleAddressSelect,
		handlePendingAddressSelect,
		handleSuggestSelect,
		handleCurrentLocation,
		handleSearchFocus,
		handleSearchChange,
		toggleDropdown,
		closeDropdown,
		formatBranchHours,
		normalizeBranchText,
		formatAddressLabel,
	} = useDeliveryTemplateSelector({
		globalData,
		isTakeaway,
		selectedAddressId,
		selectedBranchId,
		setSelectedAddressId,
		setSelectedBranchId: (id) => {
			// Reset schedule when branch changes — takeaway dates differ per branch
			if (id !== selectedBranchId) {
				setSelectedDate(null);
				setSelectedTime(null);
				setTimeSlots([]);
			}
			setSelectedBranchId(id);
		},
		pendingAddress,
		hasAddressType,
		onBeforeAddressFormOpen: markAddressFormRestore,
	});

	const selectedAddress = useMemo(() => addressList.find((a) => a.id === selectedAddressId) || null, [addressList, selectedAddressId]);
	const showStaleAddressWarning = !isTakeaway && selectedAddress && !selectedAddress.geo_coords;
	const isPendingSelected = selectedAddressId === PENDING_ID;

	// Re-point selection at the current default (or first available) address whenever
	// the previously selected one is no longer in the list — e.g. it was deleted from
	// the account address management page while this template stayed mounted.
	useEffect(() => {
		if (isTakeaway || isPendingSelected || addressLoading || !selectedAddressId) return;
		if (addressList.some((a) => a.id === selectedAddressId)) return;
		const fallback = addressList.find((a) => a.is_default) || addressList[0] || null;
		setSelectedAddressId(fallback ? fallback.id : null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addressList, addressLoading, isTakeaway, isPendingSelected, selectedAddressId]);

	const selectionErrorMessage = useMemo(() => {
		if (isTakeaway && !selectedBranchId) return 'deliveryTemplate.selectShopLocationRequired';
		if (!isTakeaway && !selectedAddress && !isPendingSelected) return 'deliveryTemplate.selectAddressRequired';
		return 'deliveryTemplate.fulfillmentSelectionRequired';
	}, [isTakeaway, selectedBranchId, selectedAddress, isPendingSelected]);

	const dateOptions = useMemo(() => {
		return toDateOptions(getAvailDates(selectedBranchId, isTakeaway));
	}, [fulfillment, selectedBranchId, isTakeaway]);

	// ASAP is delivery-only and only meaningful for today, once the backend reports
	// immediate delivery is available. It shows as the first entry in the time list.
	const showAsapOption = !isTakeaway && Boolean(fulfillment?.asap_available) && selectedDate === getTodayDateString();

	const slotOptions = useMemo(() => {
		const base = timeSlots.map((s) => ({ id: s, name: s }));
		if (showAsapOption) {
			return [{ id: ASAP_SLOT_ID, name: translate('deliveryTemplate.asap') }, ...base];
		}
		return base;
	}, [timeSlots, showAsapOption, translate]);

	const selectedDateLabel = useMemo(() => dateOptions.find((d) => d.id === selectedDate)?.name || null, [dateOptions, selectedDate]);

	const selectedTimeLabel = useMemo(() => slotOptions.find((s) => s.id === selectedTime)?.name || null, [slotOptions, selectedTime]);

	useEffect(() => {
		setShowSelectionError(false);
	}, [selectedAddressId, selectedBranchId, activeTab]);

	const closeSlotDropdowns = useCallback(() => {
		setShowDateList(false);
		setShowTimeList(false);
	}, []);

	const handleToggleDropdown = useCallback(() => {
		if (!showDropdown) closeSlotDropdowns();
		toggleDropdown();
	}, [showDropdown, toggleDropdown, closeSlotDropdowns]);

	const handleAddressSearchFocus = useCallback(() => {
		closeSlotDropdowns();
		handleSearchFocus();
	}, [closeSlotDropdowns, handleSearchFocus]);

	const handleAddressSearchChange = useCallback(
		(e) => {
			closeSlotDropdowns();
			handleSearchChange(e);
		},
		[closeSlotDropdowns, handleSearchChange]
	);

	const toggleDateList = useCallback(() => {
		setShowDateList((open) => {
			const next = !open;
			if (next) {
				closeDropdown();
				setShowTimeList(false);
			}
			return next;
		});
	}, [closeDropdown]);

	const toggleTimeList = useCallback(() => {
		setShowTimeList((open) => {
			const next = !open;
			if (next) {
				closeDropdown();
				setShowDateList(false);
			}
			return next;
		});
	}, [closeDropdown]);

	useEffect(() => {
		if (selectedDate || dateOptions.length === 0) return;
		setSelectedDate(dateOptions[0].id);
		setSelectedTime(null);
	}, [dateOptions, selectedDate]);

	useEffect(() => {
		const handler = (event) => {
			setPendingAddress(event.detail || readPendingAddress());
			if (!isTakeaway) setSelectedAddressId(PENDING_ID);
		};
		window.addEventListener('pendingAddressSaved', handler);
		return () => window.removeEventListener('pendingAddressSaved', handler);
	}, [isTakeaway]);

	useEffect(() => {
		if (isTakeaway || selectedAddressId) return;

		const contextAddressId = hasAddressType ? selectedAddresses?.shipping?.id : selectedAddresses?.id;

		if (contextAddressId) {
			setSelectedAddressId(contextAddressId);
		}
	}, [hasAddressType, isTakeaway, selectedAddressId, selectedAddresses]);

	useEffect(() => {
		if (!selectedTime || slotOptions.length === 0) return;
		if (!slotOptions.some((s) => s.id === selectedTime)) setSelectedTime(null);
	}, [slotOptions, selectedTime]);

	useEffect(() => {
		if (!selectedDate) {
			setTimeSlots([]);
			return;
		}
		const params = {
			type: isTakeaway ? 'takeaway' : 'scheduled_delivery',
			date: selectedDate,
			...(isTakeaway && selectedBranchId ? { branchId: selectedBranchId } : {}),
		};
		setSlotsLoading(true);
		api.get({ url: 'fulfillment/slots', params })
			.then((res) => {
				const slots = res?.slots || [];
				setTimeSlots(slots);
				const asapDefault = !isTakeaway && Boolean(fulfillment?.asap_available) && selectedDate === getTodayDateString();
				setSelectedTime((prev) => {
					if (prev && (prev === ASAP_SLOT_ID || slots.includes(prev))) return prev;
					if (asapDefault) return ASAP_SLOT_ID;
					return slots[0] || null;
				});
			})
			.catch(() => setTimeSlots([]))
			.finally(() => setSlotsLoading(false));
	}, [selectedDate, isTakeaway, selectedBranchId, fulfillment?.asap_available]);

	useEffect(() => {
		const handler = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target) && showDropdown) {
				closeDropdown();
			}
			if (dateRef.current && !dateRef.current.contains(e.target)) setShowDateList(false);
			if (timeRef.current && !timeRef.current.contains(e.target)) setShowTimeList(false);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [showDropdown, closeDropdown, dropdownRef]);

	const handleTabChange = useCallback(
		(tab) => {
			setActiveTab(tab);
			setSelectedDate(null);
			setSelectedTime(null);
			setTimeSlots([]);
			setShowDateList(false);
			setShowTimeList(false);
			closeDropdown();
			formMethods.clearErrors('phone');
		},
		[closeDropdown, formMethods]
	);

	const handleDateSelect = useCallback((dateId) => {
		setSelectedDate(dateId);
		setSelectedTime(null);
		setShowDateList(false);
	}, []);

	const handleTimeSelect = useCallback((timeId) => {
		setSelectedTime(timeId);
		setShowTimeList(false);
	}, []);

	const handleAddNewAddress = useCallback(() => {
		closeDropdown();
		closeSlotDropdowns();
		markAddressFormRestore();
		openPopup(
			<AddressForm
				title='account.address_management.addNewAddress'
				type={hasAddressType ? 'shipping' : undefined}
			/>,
			{ className: 'popup-address-form-block' }
		);
	}, [closeDropdown, openPopup, hasAddressType, closeSlotDropdowns, markAddressFormRestore]);

	const dismiss = useCallback(() => {
		if (!isDropdown) closePopup();
		onClose?.();
	}, [isDropdown, closePopup, onClose]);

	const handleConfirm = useCallback(() => {
		const currentPhone = formMethods.getValues('phone');
		const isAsapSelected = !isTakeaway && selectedTime === ASAP_SLOT_ID;
		fillCheckoutData({
			fulfillmentType: isTakeaway ? 'takeaway' : isAsapSelected ? 'asap' : 'delivery',
			fulfillmentBranchId: isTakeaway ? selectedBranchId : null,
			scheduledDate: isAsapSelected ? null : selectedDate,
			scheduledTime: isAsapSelected ? null : selectedTime,
			...(isTakeaway && currentPhone ? { phone: currentPhone } : {}),
		});
		if (!isTakeaway && !isPendingSelected && selectedAddress) {
			selectAddress({ checkedId: selectedAddressId, ...(hasAddressType ? { type: 'shipping' } : {}) });
		}
		dismiss();
		const navigationIntent = resolveNavigationIntent(redirectAfterConfirm);
		const routeKey = resolveRedirectRouteKey(navigationIntent);
		if (routeKey) {
			clearNavigationIntent();
			navigateWithLoading(routeKey);
		}
	}, [formMethods, fillCheckoutData, isTakeaway, selectedBranchId, selectedDate, selectedTime, selectedAddressId, selectedAddress, isPendingSelected, selectAddress, hasAddressType, dismiss, redirectAfterConfirm, navigateWithLoading]);

	const isConfirmDisabled = useMemo(() => {
		if (isTakeaway && !selectedBranchId) return true;
		if (!isTakeaway && !isPendingSelected && !selectedAddress) return true;
		if (!isTakeaway && showStaleAddressWarning) return true;
		if (dateOptions.length === 0) return false;
		if (!selectedDate) return true;
		if (slotOptions.length > 0 && !selectedTime) return true;
		return false;
	}, [isTakeaway, selectedBranchId, selectedAddress, isPendingSelected, showStaleAddressWarning, dateOptions.length, selectedDate, slotOptions.length, selectedTime]);

	const showSelectionRequiredPopup = useCallback(() => {
		if (isDropdown) {
			setShowSelectionError(true);
			return;
		}
		showFulfillmentRequiredPopup(openPopup, closePopup, {
			initialTab: isTakeaway ? 'takeaway' : 'delivery',
			redirectAfterConfirm,
		});
	}, [closePopup, isDropdown, isTakeaway, openPopup, redirectAfterConfirm]);

	// Runs only after the user confirms in the warning popup: NOW actually strip
	// the items unavailable at the new branch, refresh the cart store, then apply
	// the branch selection. Logged-in users are stripped server-side (cart?branchId
	// mutates the cart); guests have no server cart, so we remove the previewed
	// items from the local guest cart instead.
	const applyBranchChangeAndConfirm = useCallback(
		async (removedItemIds = []) => {
			startPageLoading();
			try {
				const { useCartStore } = await import('@weareconceptstudio/cart');
				if (isLoggedIn) {
					await api.get({ url: 'cart', params: { branchId: selectedBranchId } });
				} else {
					const removeItem = useCartStore.getState().removeItem;
					for (const productId of removedItemIds) {
						// eslint-disable-next-line no-await-in-loop
						await removeItem?.({ productId });
					}
				}
				useCartStore.getState().getCart?.();
			} catch {
				// continue to confirm even if the refresh fails
			} finally {
				stopPageLoading();
			}
			handleConfirm();
		},
		[isLoggedIn, selectedBranchId, startPageLoading, stopPageLoading, handleConfirm]
	);

	const handleConfirmClick = useCallback(async () => {
		if (isConfirmDisabled) {
			showSelectionRequiredPopup();
			return;
		}

		if (isTakeaway) {
			const isPhoneValid = await formMethods.trigger('phone');
			if (!isPhoneValid) return;
		}

		const branchChanged = isTakeaway && checkoutData?.fulfillmentBranchId != null && String(checkoutData.fulfillmentBranchId) !== String(selectedBranchId);

		if (branchChanged) {
			// Dry-run: ask the backend whether switching to this branch WOULD remove
			// items, without actually modifying the cart yet. Logged-in users are
			// previewed against their server cart; guests send their local items to
			// the stateless guest preview endpoint.
			startPageLoading();
			let willUpdate = false;
			let removedItemIds = [];
			let removedItemNames = [];
			try {
				if (isLoggedIn) {
					const res = await api.get({
						url: 'cart/takeaway-branch-preview',
						params: { branchId: selectedBranchId },
					});
					willUpdate = Boolean(res?.cart_will_update);
					removedItemNames = (res?.removed_items || []).map((it) => it?.name).filter(Boolean);
				} else {
					const { useCartStore } = await import('@weareconceptstudio/cart');
					const guestItems = (useCartStore.getState().items || []).map((it) => ({ productId: it?.product?.id ?? it?.productId })).filter((it) => it.productId);
					const res = await api.post('guest-cart/takeaway-branch-preview', {
						branchId: selectedBranchId,
						items: guestItems,
					});
					const data = res?.data ?? res;
					willUpdate = Boolean(data?.cart_will_update);
					removedItemIds = (data?.removed_items || []).map((it) => it?.id).filter(Boolean);
					removedItemNames = (data?.removed_items || []).map((it) => it?.name).filter(Boolean);
				}
			} catch {
				// preview failed — fall through and apply the change normally
			}
			stopPageLoading();

			if (willUpdate) {
				// Ask first, listing the items that will be removed. The cart is
				// only mutated after the user confirms.
				openPopup(
					<CartModifiedWarningPopup
						onConfirm={() => applyBranchChangeAndConfirm(removedItemIds)}
						removedItemNames={removedItemNames}
					/>,
					{
						className: 'cart-modification-warning-popup',
					}
				);
				return;
			}
		}

		handleConfirm();
	}, [isTakeaway, isLoggedIn, formMethods, checkoutData?.fulfillmentBranchId, selectedBranchId, openPopup, handleConfirm, applyBranchChangeAndConfirm, isConfirmDisabled, showSelectionRequiredPopup, startPageLoading, stopPageLoading]);

	const autoConfirmedRef = useRef(false);

	useEffect(() => {
		if (!autoConfirmWhenReady || isConfirmDisabled || autoConfirmedRef.current) return;

		const timer = window.setTimeout(() => {
			if (autoConfirmedRef.current || isConfirmDisabled) return;
			autoConfirmedRef.current = true;
			handleConfirmClick();
		}, 0);

		return () => window.clearTimeout(timer);
	}, [autoConfirmWhenReady, handleConfirmClick, isConfirmDisabled]);

	return (
		<DeliveryTemplateStyle className={isDropdown ? 'is-sticky-dropdown' : ''}>
			{!isDropdown && (
				<Text
					className='h6 dt-title font-montserrat-arm-medium background-color'
					text={isTakeaway ? 'deliveryTemplate.takeawayTitle' : 'deliveryTemplate.title'}
				/>
			)}

			<div className='delivery-bar-wrapper'>
				<div className='outside-wrapper-tabs'>
					<div
						className='wrapper-tabs'
						onMouseDown={(e) => e.stopPropagation()}
						onTouchStart={(e) => e.stopPropagation()}
					>
						<Button
							btnType='category'
							className={activeTab === 'delivery' ? 'active' : ''}
							onClick={() => handleTabChange('delivery')}
						>
							<Text
								className='p p4 font-montserrat-arm-medium'
								text='deliveryTemplate.deliveryTab'
							/>
						</Button>
						<Button
							btnType='category'
							className={activeTab === 'takeaway' ? 'active' : ''}
							onClick={() => handleTabChange('takeaway')}
						>
							<Text
								className='p p4 font-montserrat-arm-medium'
								text='deliveryTemplate.takeawayTab'
							/>
						</Button>
					</div>
				</div>

				<div
					className='wrapper-selector'
					ref={dropdownRef}
					onMouseDown={(e) => e.stopPropagation()}
					onTouchStart={(e) => e.stopPropagation()}
				>
					{isTakeaway ? (
						<div
							className='selector-button'
							onClick={handleToggleDropdown}
						>
							<Icon
								name='location'
								className='icon-pin'
							/>
							<Text className='selector-text font-montserrat-arm-regular background-color'>{takeawayPlaceholder}</Text>
							<Icon
								className={`icon-arrow ${showDropdown ? 'is-open' : ''}`}
								name='right-arrow'
							/>
						</div>
					) : (
						<div
							className='selector-field'
							onClick={handleToggleDropdown}
						>
							<Icon
								name='location'
								className='icon-pin'
							/>
							<div
								className='selector-input-wrap'
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								<Form.Item
									name={DELIVERY_SEARCH_FIELD}
									required={false}
								>
									<Input
										name={DELIVERY_SEARCH_FIELD}
										placeholder='deliveryTemplate.whatsYourAddressPlaceholder'
										onFocus={handleAddressSearchFocus}
										onChange={handleAddressSearchChange}
									/>
								</Form.Item>
							</div>
							<Icon
								className={`icon-arrow ${showDropdown ? 'is-open' : ''}`}
								name='right-arrow'
							/>
						</div>
					)}

					{showDropdown && (
						<div className='wrapper-dropdown takeaway-dropdown'>
							{isTakeaway ? (
								branches.map((branch, index) => {
									const isSelected = selectedBranchId ? Number(selectedBranchId) === Number(branch.id) : index === 0;

									return (
										<div
											key={branch.id}
											className={`dropdown-item ${isSelected ? 'active' : ''}`}
											onClick={() => handleBranchSelect(branch)}
										>
											<Icon
												name='location'
												className='icon-pin background-color'
											/>
											<div className='item-info'>
												<Text className='p p3 font-montserrat-arm-medium background-color'>{normalizeBranchText(branch.name)}</Text>
												{branch.address && <Text className='p p4 font-montserrat-arm-regular background-color'>{normalizeBranchText(branch.address)}</Text>}
												{branch.open_time && branch.close_time && (
													<div className='item-hours'>
														<Text
															className='p p5 font-montserrat-arm-medium item-status background-color'
															text='deliveryTemplate.open'
														/>
														<Text className='p p5 font-montserrat-arm-medium background-color'>
															{formatBranchHours(branch.open_time)} - {formatBranchHours(branch.close_time)}
														</Text>
													</div>
												)}
											</div>
										</div>
									);
								})
							) : (
								<>
									{isSearching ? (
										<>
											{isSuggestLoading ? (
												<Text
													className='p p4 font-montserrat-arm-regular empty-state background-color'
													text='deliveryTemplate.loading'
												/>
											) : addressSuggestions.length > 0 ? (
												addressSuggestions.map((suggestion, index) => (
													<div
														key={suggestion.uri || `${suggestion.title?.text}-${index}`}
														className='dropdown-item'
														onClick={() => handleSuggestSelect(suggestion)}
													>
														<div className='item-info'>
															<Text className='p p4 font-montserrat-arm-medium background-color'>{suggestion.title?.text}</Text>
															{suggestion.address?.formatted_address && <Text className='p p5 font-montserrat-arm-regular background-color'>{suggestion.address.formatted_address}</Text>}
														</div>
													</div>
												))
											) : (
												<Text
													className='p p4 font-montserrat-arm-regular empty-state background-color'
													text='deliveryTemplate.noAddressesFound'
												/>
											)}
										</>
									) : (
										<>
											<div
												className='location-button'
												onClick={handleCurrentLocation}
												disabled={isLocating}
											>
												<Icon
													name='gps'
													className='item-icon background-color'
												/>
												<Text
													className='p p3 font-montserrat-arm-medium background-color'
													text={isLocating ? 'deliveryTemplate.locating' : 'deliveryTemplate.deliveryToMyAddress'}
												/>
											</div>
											{addressLoading ? (
												<Text
													className='p p4 font-montserrat-arm-regular empty-state background-color'
													text='deliveryTemplate.loading'
												/>
											) : addressList.length > 0 ? (
												addressList.map((address) => (
													<div
														key={address.id}
														className={`dropdown-item ${selectedAddressId === address.id ? 'active' : ''}`}
														onClick={() => handleAddressSelect(address)}
													>
														<Icon
															name='location'
															className='icon-pin background-color'
														/>
														<div className='item-info'>
															<Text className='p p3 font-montserrat-arm-medium background-color'>{address.name || formatAddressLabel(address)}</Text>
															{address.name && <Text className='p p4 font-montserrat-arm-regular background-color'>{formatAddressLabel(address)}</Text>}
														</div>
													</div>
												))
											) : null}
										</>
									)}

									{!isLoggedIn &&
										formatAddressLabel(pendingAddress) &&
										(() => {
											const primaryLabel = formatAddressTitle(pendingAddress);
											const subLabel = formatAddressSubtitle(pendingAddress);

											return (
												<div
													className={`dropdown-item ${isPendingSelected ? 'active' : ''}`}
													onClick={handlePendingAddressSelect}
												>
													<Icon
														name='location'
														className='icon-pin background-color'
													/>
													<div className='item-info'>
														<Text className='p p3 font-montserrat-arm-medium background-color'>{primaryLabel}</Text>
														{subLabel && <Text className='p p4 font-montserrat-arm-regular background-color'>{subLabel}</Text>}
													</div>
												</div>
											);
										})()}
								</>
							)}
						</div>
					)}
				</div>
			</div>

			{showSelectionError && (
				<div className='dt-selection-error'>
					<Text
						className='p p5 font-montserrat-arm-regular dt-selection-error-text'
						text={selectionErrorMessage}
					/>
				</div>
			)}

			{showStaleAddressWarning && (
				<div className='dt-stale-address-warning'>
					<Icon
						name='warning'
						className='dt-stale-address-icon'
					/>
					<Text
						className='p p5 font-montserrat-arm-regular dt-stale-address-text'
						text='deliveryTemplate.updateAddressForDelivery'
					/>
					<button
						type='button'
						className='dt-stale-address-edit'
						onClick={handleAddNewAddress}
					>
						<Text
							className='p p5 font-montserrat-arm-medium'
							text='deliveryTemplate.updateAddressAction'
						/>
					</button>
				</div>
			)}

			{dateOptions.length > 0 && <div className='dt-divider' />}

			{dateOptions.length > 0 && (
				<>
					<Text
						className='p p4 font-montserrat-arm-medium dt-slot-label'
						text='deliveryTemplate.deliverySlot'
					/>
					<div className='dt-time-row'>
						<div
							className='dt-select-wrap'
							ref={dateRef}
						>
							<button
								type='button'
								className='dt-select-btn'
								onClick={toggleDateList}
							>
								<Text className={`p p4 font-montserrat-arm-medium dt-select-label background-color ${!selectedDateLabel ? 'placeholder' : ''}`}>{selectedDateLabel || translate('deliveryTemplate.selectDate')}</Text>
								<Icon
									name='right-arrow'
									className={`icon-arrow ${showDateList ? 'is-open' : ''}`}
								/>
							</button>
							{showDateList && (
								<div className='wrapper-dropdown'>
									{dateOptions.map((opt) => (
										<div
											key={opt.id}
											className={`dropdown-item ${selectedDate === opt.id ? 'active' : ''}`}
											onClick={() => handleDateSelect(opt.id)}
										>
											<Text className='p p4 font-montserrat-arm-medium background-color'>{opt.name}</Text>
										</div>
									))}
								</div>
							)}
						</div>

						<div
							className='dt-select-wrap'
							ref={timeRef}
						>
							<button
								type='button'
								className='dt-select-btn'
								disabled={!selectedDate || slotsLoading}
								onClick={toggleTimeList}
							>
								<Text className={`p p4 font-montserrat-arm-medium dt-select-label background-color ${!selectedTimeLabel ? 'placeholder' : ''}`}>{slotsLoading ? translate('deliveryTemplate.loading') : selectedTimeLabel || translate('deliveryTemplate.selectTime')}</Text>
								<Icon
									name='right-arrow'
									className={`icon-arrow ${showTimeList ? 'is-open' : ''}`}
								/>
							</button>
							{showTimeList && slotOptions.length > 0 && (
								<div className='wrapper-dropdown'>
									{slotOptions.map((opt) => (
										<div
											key={opt.id}
											className={`dropdown-item ${selectedTime === opt.id ? 'active' : ''}`}
											onClick={() => handleTimeSelect(opt.id)}
										>
											<Text className='p p4 font-montserrat-arm-medium background-color'>{opt.name}</Text>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</>
			)}

			{isTakeaway && (
				<div className={'takeaway-phone-wrap'}>
					<div className='dt-phone-wrap'>
						<Form.Item
							name='phone'
							label='phone'
						>
							<Input.PhoneNumber
								name='phone'
								placeholder='deliveryTemplate.phonePlaceholder'
								onChange={() => {
									formMethods.clearErrors('phone');
								}}
							/>
						</Form.Item>
					</div>
				</div>
			)}

			<div className={`dt-actions ${isTakeaway ? 'is-takeaway' : ''}`}>
				{!isTakeaway && (
					<Button
						btnType='text-link'
						className={isDropdown ? 'dt-add-address' : ''}
						text='deliveryTemplate.addNewAddress'
						onClick={handleAddNewAddress}
					/>
				)}
				{isDropdown ? (
					<div className='dt-actions-primary'>
						<Button
							btnType='reverse'
							className='full'
							text='deliveryTemplate.confirm'
							onClick={handleConfirmClick}
						/>
						<Button
							btnType='text-link'
							text='deliveryTemplate.cancel'
							onClick={dismiss}
						/>
					</div>
				) : (
					<div className='dt-action-right'>
						<Button
							btnType='text-link'
							text='deliveryTemplate.cancel'
							onClick={dismiss}
						/>
						<Button
							btnType='reverse'
							text='deliveryTemplate.confirm'
							onClick={handleConfirmClick}
						/>
					</div>
				)}
			</div>
		</DeliveryTemplateStyle>
	);
};

const DeliveryTemplate = ({ initialTab, globalData, redirectAfterConfirm, onClose, variant = 'popup', autoConfirmWhenReady = false }) => (
	<Form
		initialValues={{ [DELIVERY_SEARCH_FIELD]: '' }}
		mode='onBlur'
		onSubmit={async () => {}}
	>
		<DeliveryTemplateContent
			initialTab={initialTab}
			globalData={globalData}
			redirectAfterConfirm={redirectAfterConfirm}
			onClose={onClose}
			variant={variant}
			autoConfirmWhenReady={autoConfirmWhenReady}
		/>
	</Form>
);

export default withDataContext(DeliveryTemplate, ['globalData']);
